import { type RectReadOnly } from 'react-use-measure';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { userApi } from '@/api/queries';
import { diceGameApi } from '@/api/queries/game';
import { generateMiniGameCardsOptions } from '@/common/lib/mini-game';
import { type CommoditiesType } from '@/common/types/common';
import {
  type BoxRewardsType,
  type BuyEnergyForDiceGameResponse,
  type DiceGameTileType,
  type DiceGameUserDataType,
  type GetGameBoardResponseType,
  type MiniGameBoardType,
  type MiniGameCardsOptions,
  type MiniGameDataType,
  type MiniGameDiceRollResponseType,
  type OpenCardsCellResponseType,
  type RollDiceResponseType,
} from '@/common/types/dice-game';

interface DiceGameStateType {
  user: DiceGameUserDataType | null;
  board: DiceGameTileType[];
  miniGame: MiniGameDataType | null;
  isGameInactive?: boolean;
  nextEventStartsAt: null | string;
  dice?: number;
  position: {
    bombImage: { x: number; y: number } | null;
    rewards: Partial<
      Record<CommoditiesType | 'cash' | 'str' | 'tgem', RectReadOnly>
    >;
  };
  tileAnimationSteps: null | number[][];
  cardsMiniGameOptions: null | MiniGameCardsOptions;
  activeBoard: MiniGameBoardType;
  boxReward: BoxRewardsType | null;
  rollPower: 1 | 2 | 5;
  currentRollPower: 1 | 2 | 5;
  skipAnimation: boolean;
}

const initialState: DiceGameStateType = {
  user: null,
  board: [],
  miniGame: null,
  nextEventStartsAt: null,
  position: {
    bombImage: null,
    rewards: {},
  },
  tileAnimationSteps: null,
  cardsMiniGameOptions: null,
  activeBoard: null,
  rollPower: 1,
  currentRollPower: 1,
  boxReward: null,
  skipAnimation: false,
};

const diceGameSlice = createSlice({
  name: 'diceGame',
  initialState,
  reducers: {
    setDiceResult: (
      state,
      { payload }: PayloadAction<RollDiceResponseType>,
    ) => {
      if (state.user) {
        state.user.position = payload.newPosition;
        state.user.energyRecoveryTime = payload.energyRecoveryTime;

        if (payload.cellType !== 'Energy') {
          state.user.energy = payload.energy;
        }

        if (payload.cellType !== 'dice') {
          state.user.diceCount = payload.diceCount;
        }
      }

      if (payload.miniGame) {
        state.miniGame = { ...payload.miniGame, position: payload.newPosition };
      }
    },
    setMiniGameDiceResult: (
      state,
      { payload }: PayloadAction<MiniGameDiceRollResponseType>,
    ) => {
      if (state.miniGame) {
        state.miniGame.movesLeft = payload.movesLeft;
        state.miniGame.position = payload.newPosition;
        state.miniGame.reward = payload.reward;
        state.miniGame.movesLeft = payload.movesLeft;
      }
    },
    rechargeOneDiceGameEnergy: (
      state,
      { payload }: PayloadAction<{ vip: boolean }>,
    ) => {
      if (state.user && state.user.energy !== undefined) {
        const energy = state.user.energy;

        if ((energy < 9 && !payload.vip) || (energy < 14 && payload.vip)) {
          state.user.energyRecoveryTime = 15 * 60;
        } else {
          state.user.energyRecoveryTime = 0;
        }

        if ((energy < 10 && !payload.vip) || (energy < 15 && payload.vip)) {
          ++state.user.energy;
        }
      }
    },
    claimDiceReward: (state) => {
      if (state.user) {
        state.user.diceCount += 2;
      }
    },
    addToDiceCount: (state, { payload }: PayloadAction<number>) => {
      if (state.user) {
        state.user.diceCount += payload;
      }
    },
    setDiceCount: (state, { payload }: PayloadAction<number>) => {
      if (state.user) {
        state.user.diceCount = payload;
      }
    },
    setBombImagePosition: (
      state,
      { payload }: PayloadAction<{ x: number; y: number } | null>,
    ) => {
      state.position.bombImage = payload;
    },
    setClaimRewardAnimationFinalPosition: (
      state,
      {
        payload,
      }: PayloadAction<{
        reward: CommoditiesType | 'cash' | 'str' | 'tgem';
        position: RectReadOnly;
      }>,
    ) => {
      state.position.rewards[payload.reward] = payload.position;
    },
    setTileAnimationSteps: (state, { payload }: PayloadAction<number[][]>) => {
      state.tileAnimationSteps = payload;
    },
    changeDiceEnergy: (state, { payload }: PayloadAction<number>) => {
      if (state.user) {
        state.user.energy += payload;
      }
    },
    setDiceEnergy: (state, { payload }: PayloadAction<number>) => {
      if (state.user) {
        state.user.energy = payload;
      }
    },
    changeDiceCount: (state, { payload }: PayloadAction<number>) => {
      if (state.user) {
        state.user.diceCount += payload;
      }
    },
    setMiniGameCardsOptions: (
      state,
      { payload }: PayloadAction<MiniGameCardsOptions | null>,
    ) => {
      state.cardsMiniGameOptions = payload;
    },
    reduceMiniGameCardOption: (
      state,
      { payload }: PayloadAction<keyof MiniGameCardsOptions>,
    ) => {
      if (state.cardsMiniGameOptions) {
        state.cardsMiniGameOptions[payload] -= 1;
      }
    },
    setActiveBoard: (state, { payload }: PayloadAction<MiniGameBoardType>) => {
      state.activeBoard = payload;
    },
    setRollPower: (state, { payload }: PayloadAction<1 | 2 | 5>) => {
      state.rollPower = payload;
    },
    setCurrentRollPower: (state, { payload }: PayloadAction<1 | 2 | 5>) => {
      state.currentRollPower = payload;
    },
    setBoxReward: (
      state,
      { payload }: PayloadAction<BoxRewardsType | null>,
    ) => {
      state.boxReward = payload;
    },
    toggleSkipDiceAnimation: (state) => {
      state.skipAnimation = !state.skipAnimation;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        diceGameApi.endpoints.getGameBoard.matchFulfilled,
        (state, { payload }: PayloadAction<GetGameBoardResponseType>) => {
          if ('board' in payload) {
            state.user = payload.user;
            state.board = payload.board;

            if (payload.miniGame) {
              state.miniGame = payload.miniGame;
              state.currentRollPower =
                payload.miniGame.rollPower ?? payload.rollPower;

              const cardsTileList = ['cash', 'Resource', 'Material', 'Product'];

              if (cardsTileList.includes(payload.miniGame.type)) {
                state.cardsMiniGameOptions = generateMiniGameCardsOptions();
                state.miniGame?.reward.forEach((reward) => {
                  if (typeof reward === 'string') {
                    const key = Object.keys(state.cardsMiniGameOptions!).find(
                      (key) => reward.includes(key),
                    );

                    if (!key) return;
                    state.cardsMiniGameOptions![
                      key as keyof MiniGameCardsOptions
                    ] -= 1;
                  }
                });
              }
            }

            state.nextEventStartsAt = null;
            state.isGameInactive = false;
            state.dice = undefined;
          } else {
            state.nextEventStartsAt = payload.nextEventStartsAt;
            state.isGameInactive = true;
            state.dice = payload.dice;
            state.user = null;
            state.board = [];
            state.miniGame = null;
          }
        },
      )
      .addMatcher(
        diceGameApi.endpoints.miniGameExit.matchFulfilled,
        (state) => {
          if (state.miniGame) {
            state.miniGame = null;
          }
        },
      )
      .addMatcher(
        diceGameApi.endpoints.openCardsCell.matchFulfilled,
        (state, { payload }: PayloadAction<OpenCardsCellResponseType>) => {
          if (state.miniGame) {
            state.miniGame.rewardCollected = payload.rewardCollected;
          }
        },
      )
      .addMatcher(
        diceGameApi.endpoints.buyEnergyForDiceGame.matchFulfilled,
        (state, { payload }: PayloadAction<BuyEnergyForDiceGameResponse>) => {
          if (state.user) {
            state.user.energy = payload.energy;
            state.user.energyRecoveryTime = 0;
          }
        },
      )
      .addMatcher(
        userApi.endpoints.claimThankYouReward.matchFulfilled,
        (state, action) => {
          if (typeof state.dice === 'number') {
            state.dice += action.meta.arg.originalArgs.dice ?? 0;
          }

          if (typeof state.user?.diceCount === 'number') {
            state.user.diceCount += action.meta.arg.originalArgs.dice ?? 0;
          }
        },
      )
      .addMatcher(
        userApi.endpoints.getMyInfo.matchFulfilled,
        (state, { payload }) => {
          state.dice = payload.balance.dice;

          if (state.user) {
            state.user.diceCount = payload.balance.dice;
          }
        },
      );
  },
});

export const {
  setDiceResult,
  rechargeOneDiceGameEnergy,
  setMiniGameDiceResult,
  claimDiceReward,
  addToDiceCount,
  setBombImagePosition,
  setClaimRewardAnimationFinalPosition,
  setTileAnimationSteps,
  changeDiceEnergy,
  changeDiceCount,
  setMiniGameCardsOptions,
  reduceMiniGameCardOption,
  setActiveBoard,
  setRollPower,
  setDiceCount,
  setDiceEnergy,
  setCurrentRollPower,
  setBoxReward,
  toggleSkipDiceAnimation,
} = diceGameSlice.actions;
export default diceGameSlice.reducer;
